<template>
  <div class="cfa-root">
    <c-f-a-header></c-f-a-header>
    <div class="cfa-root-container">
      <div v-if="loading" class="content d-flex align-items-center justify-content-center">
        <div class="text-center">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Chargement du certificat...</p>
        </div>
      </div>

      <div v-else-if="certificate" class="content d-flex align-items-center">
        <div class="container">
            <div class="row section_content shadow-sm rounded">
                <!-- Left Column: Preview -->
                <div class="col-12 col-md-5 d-flex align-items-center justify-content-center bg-light p-4">
                    <div class="position-relative text-center w-100">
                        <img 
                            v-if="certificate.preview_url" 
                            :src="certificate.preview_url" 
                            class="img-fluid rounded shadow-sm certificate-preview"
                            alt="Certificat Preview"
                        />
                         <div v-else class="text-center py-5">
                            <i class="fa fa-file-pdf fa-5x text-muted"></i>
                        </div>
                        
                         <a :href="certificate.pdf_url" target="_blank" class="btn btn-outline-primary mt-3 d-block d-md-inline-block">
                            <i class="fa fa-eye"></i> {{ $t('view_full_pdf') || 'Voir le PDF' }}
                        </a>
                    </div>
                </div>

                <!-- Right Column: Details -->
                <div class="col-12 col-md-7 p-4 text-start">
                    <div class="mb-3">
                         <span class="badge bg-success p-2">
                            <i class="fa fa-check-circle"></i> {{ $t('verified_certificate') || 'Certificat Vérifié' }}
                        </span>
                    </div>

                    <h1 class="h2 mb-2 text-dark">{{ certificate.firstname }} {{ certificate.lastname }}</h1>
                    <p class="text-muted text-uppercase small mb-4 font-weight-bold">
                        {{ $t('certified_cybersecurity_professional') || 'Professionnel Certifié Cybersécurité' }}
                    </p>

                    <div class="card bg-light border-0 mb-4">
                        <div class="card-body d-flex align-items-center">
                            <div class="rounded-circle bg-white p-3 shadow-sm me-3">
                                <i class="fa fa-trophy text-primary fa-lg"></i>
                            </div>
                            <div>
                                <h6 class="text-muted mb-0">Cyberscore</h6>
                                <h3 class="text-primary mb-0 fw-bold">{{ certificate.note }}%</h3>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-4">
                        <div class="col-6">
                            <small class="text-muted d-block">Date d'obtention</small>
                            <span class="fw-bold">{{ formatDate(certificate.date) }}</span>
                        </div>
                        <div class="col-6">
                            <small class="text-muted d-block">ID du Certificat</small>
                            <span class="font-monospace bg-light px-2 py-1 rounded">{{ certificate.code }}</span>
                        </div>
                    </div>

                    <hr class="my-4">

                    <a :href="certificate.pdf_url" class="btn cfa-btn-primary w-100 py-3" download>
                        <i class="fa fa-download me-2"></i> {{ $t('download_certificate') || 'Télécharger le certificat' }}
                    </a>
                </div>
            </div>
        </div>
      </div>

       <div v-else class="content d-flex align-items-center justify-content-center">
            <div class="text-center">
                <i class="fa fa-exclamation-circle fa-4x text-muted mb-3"></i>
                <h3>Certificat introuvable</h3>
                <p>Ce certificat n'existe pas ou n'est plus accessible.</p>
                <router-link to="/" class="btn btn-primary mt-3">Retour à l'accueil</router-link>
            </div>
       </div>

    </div>
    <c-f-a-footer></c-f-a-footer>
  </div>
</template>

<script>
import CFAFooter from '@/components/nav/CFAFooter.vue'
import CFAHeader from '@/components/nav/CFAHeader.vue'
import { getAPI } from "@/axios-api";

export default {
  name: 'PublicCertificateView',
  components: {
      CFAFooter,
      CFAHeader,
  },
  data() {
    return {
      certificate: null,
      loading: true
    }
  },
  
  async mounted() {
    await this.loadCertificate();
  },
  
  methods: {
    async loadCertificate() {
      this.loading = true;
      try {
        const certificateId = this.$route.params.id;
        const response = await getAPI.get(
          `/certificats/public/${certificateId}/`
        );
        
        if (response.status === 200 && response.data) {
           this.certificate = response.data;
        }
      } catch (error) {
        console.error('Erreur chargement certificat', error);
        this.certificate = null;
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    }
  }
}
</script>

<style scoped>
.cfa-root-container {
    min-height: calc(100vh - 185px); /* Adjust based on header/footer height */
    background-color: #f6f6f6;
    padding-top: 2em;
    padding-bottom: 2em;
}

.content {
    min-height: 80vh;
}

.section_content {
    min-height: 60vh;
    background-color: #FFF;
    overflow: hidden; /* For rounded corners on children */
}

/* Custom CFA Button style if not global */
.cfa-btn-primary {
    background-color: var(--primary-color, #4bd1b9);
    color: #fff;
    border: none;
    font-weight: 600;
}
.cfa-btn-primary:hover {
    background-color: #3ab5a0;
    color: #fff;
}

.text-primary {
    color: var(--primary-color, #4bd1b9) !important;
}

.btn-outline-primary {
    color: var(--primary-color, #4bd1b9);
    border-color: var(--primary-color, #4bd1b9);
}

.btn-outline-primary:hover {
    background-color: var(--primary-color, #4bd1b9);
    border-color: var(--primary-color, #4bd1b9);
    color: #fff;
}

.certificate-preview {
    max-height: 400px;
    object-fit: contain;
    transition: transform 0.3s ease;
}

.certificate-preview:hover {
    transform: scale(1.02);
}
</style>
